// ./src/contexts/UserProfileContext/UserProfileContext.tsx
import React, { createContext, useContext, useMemo } from "react";

import {
  DISTANCE_UNIT_MAP,
  VOLUME_UNIT_MAP,
  CONSUMPTION_UNIT_MAP,
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_VOLUME_UNIT,
  DEFAULT_CONSUMPTION_UNIT,
  TEMPERATURE_UNIT_MAP,
  DEFAULT_CURRENCY,
  DEFAULT_NOTIFY_IN_DAYS,
  DEFAULT_NOTIFY_IN_MILEAGE,
} from "@/config";

import { useUserProfile, UseUserProfileReturn } from "@/hooks/useUserProfile";

// ============================================
// Types
// ============================================

export interface UserProfileContextValue extends UseUserProfileReturn {
  // Quick access to preferences with defaults
  currency: string;
  distanceUnit: string;
  volumeUnit: string;
  consumptionUnit: string;
  temperatureUnit: string;
  notifyInMileage: number;
  notifyInDays: number;

  // Unit system detection
  isMetric: boolean;
  isImperial: boolean;

  // Display labels for units
  distanceLabel: string;
  distanceLabelShort: string;
  volumeLabel: string;
  volumeLabelShort: string;
  consumptionLabel: string;
  consumptionLabelShort: string;
  temperatureLabel: string;
}

export interface UserProfileProviderProps {
  children: React.ReactNode;
  /** Whether to automatically load data (default: true) */
  autoLoad?: boolean;
}

// ============================================
// Context
// ============================================

const UserProfileContext = createContext<UserProfileContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({
  children,
  autoLoad = true,
}) => {
  // Hook now gets userId internally from useApp()
  const userProfileHook = useUserProfile({ autoLoad });
  const { userProfile } = userProfileHook;

  // ============================================
  // Memoized context value
  // ============================================

  const contextValue = useMemo<UserProfileContextValue>(() => {
    // Preference values with defaults
    const currency = userProfile?.homeCurrency || DEFAULT_CURRENCY;
    const distanceUnit = userProfile?.distanceIn || DEFAULT_DISTANCE_UNIT;
    const volumeUnit = userProfile?.volumeIn || DEFAULT_VOLUME_UNIT;
    const consumptionUnit =
      userProfile?.consumptionIn || DEFAULT_CONSUMPTION_UNIT;
    const notifyInMileage =
      userProfile?.notifyInMileage ?? DEFAULT_NOTIFY_IN_MILEAGE;
    const notifyInDays = userProfile?.notifyInDays ?? DEFAULT_NOTIFY_IN_DAYS;

    // Unit system detection
    // Metric: km + liters
    // Imperial: miles + gallons (US or UK)
    const isMetric = distanceUnit === "km" && volumeUnit === "l";
    const isImperial =
      distanceUnit === "mi" &&
      (volumeUnit === "gal-us" || volumeUnit === "gal-uk");

    const temperatureUnit = isMetric ? "C" : "F";

    // Get display labels from unit maps
    const distanceUnitInfo = DISTANCE_UNIT_MAP[distanceUnit];
    const volumeUnitInfo = VOLUME_UNIT_MAP[volumeUnit];
    const consumptionUnitInfo = CONSUMPTION_UNIT_MAP[consumptionUnit];
    const temperatureUnitInfo = TEMPERATURE_UNIT_MAP[temperatureUnit];

    const distanceLabel = distanceUnitInfo?.labelKey || "Kilometers";
    const distanceLabelShort = distanceUnitInfo?.labelShortKey || "km";
    const volumeLabel = volumeUnitInfo?.labelKey || "Liters";
    const volumeLabelShort = volumeUnitInfo?.labelShortKey || "L";
    const consumptionLabel = consumptionUnitInfo?.labelKey || "L/100km";
    const consumptionLabelShort =
      consumptionUnitInfo?.labelShortKey || "L/100km";
    const temperatureLabel = temperatureUnitInfo?.labelShortKey || "C";

    return {
      // Spread all hook return values
      ...userProfileHook,

      // Quick access preferences
      currency,
      distanceUnit,
      volumeUnit,
      consumptionUnit,
      temperatureUnit,
      notifyInMileage,
      notifyInDays,

      // Unit system detection
      isMetric,
      isImperial,

      // Display labels
      distanceLabel,
      distanceLabelShort,
      volumeLabel,
      volumeLabelShort,
      consumptionLabel,
      consumptionLabelShort,
      temperatureLabel,
    };
  }, [userProfileHook, userProfile]);

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * Hook to access user profile (preferences) from context.
 * Must be used within UserProfileProvider.
 *
 * Provides:
 * - User profile data and loading states
 * - Quick access to preferences with defaults (currency, units, etc.)
 * - Unit system detection (isMetric, isImperial)
 * - Display labels for units
 * - Error handling via ApiError (lastError?.isAuth, lastError?.message, etc.)
 */
export const useUserProfileContext = (): UserProfileContextValue => {
  const context = useContext(UserProfileContext);

  if (context === undefined) {
    throw new Error(
      "useUserProfileContext must be used within a UserProfileProvider"
    );
  }

  return context;
};